<template>
    <kt-modal 
        :visible="visible" 
        title="Management Review"
        size="modal-lg"
        @dismiss="$emit('close')"
    >
        <template #body>
            <div class="management-review-form">
                <!-- Row 1: Technician, Work Complete Date, Request ID -->
                <div class="row mb-3">
                    <div class="col-md-4">
                        <label class="form-label"><strong>Work Details For:</strong></label>
                        <tech-ac v-model="formData.TechnicianId" />
                    </div>
                    <div class="col-md-4">
                        <label class="form-label"><strong>Work Complete Date:</strong></label>
                        <input 
                            type="date" 
                            class="form-control form-control-sm"
                            v-model="formData.WorkCompleteDate"
                        />
                    </div>
                    <div class="col-md-4">
                        <label class="form-label"><strong>Request ID:</strong></label>
                        <div class="form-control-plaintext">
                            <button 
                                v-if="formData.RequestId"
                                type="button"
                                class="btn btn-outline-primary p-0 text-decoration-none form-control"
                                @click="openJobModal"
                            >
                                <span class="fw-bold">#{{ formData.RequestId }}</span>
                                <i class="fas fa-chevron-right ms-2" style="font-size: 0.85rem;"></i>
                            </button>
                            <span v-else class="text-muted">N/A</span>
                        </div>
                    </div>
                </div>

                <!-- Row 2: Assignment with Roll Task Forward button -->
                <div class="row mb-3">
                    <div class="col-10">
                        <label class="form-label"><strong>Work Assignment:</strong></label>
                        <textarea 
                            class="form-control"
                            v-model="formData.Assignment"
                            rows="4"
                            placeholder="Work assignment description..."
                        ></textarea>
                    </div>
                    <div class="col-2">
                        <label class="form-label">&nbsp;</label>
                        <button 
                            type="button" 
                            class="btn btn-outline-info btn-sm w-100"
                            @click="rollTaskForward"
                            style="height: calc(100% - 1.5rem);"
                            title="Copy assignment to follow-up task"
                        >
                            <i class="fas fa-arrow-down me-1"></i>
                            Roll Task Forward
                        </button>
                    </div>
                </div>

                <!-- Row 3: Actual Work (full width) -->
                <div class="row mb-3">
                    <div class="col-12">
                        <label class="form-label"><strong>Actual Work:</strong></label>
                        <textarea 
                            class="form-control"
                            v-model="formData.ActualWork"
                            rows="4"
                            placeholder="Description of work performed..."
                        ></textarea>
                    </div>
                </div>

                <!-- Row 4: Invoice Text (full width) -->
                <div class="row mb-3">
                    <div class="col-12">
                        <label class="form-label"><strong>Invoice Text:</strong></label>
                        <textarea 
                            class="form-control"
                            v-model="formData.InvoiceText"
                            rows="4"
                            placeholder="Text to appear on invoice..."
                        ></textarea>
                    </div>
                </div>

                <!-- Row 5: ActualTime, ActualOvertime, ShouldBill, Status -->
                <div class="row mb-3">
                    <div class="col-md-3">
                        <label class="form-label"><strong>Actual Time (hrs):</strong></label>
                        <input 
                            type="number" 
                            class="form-control form-control-sm"
                            v-model="formData.ActualTime"
                            step="0.25"
                            min="0"
                        />
                    </div>
                    <div class="col-md-3">
                        <label class="form-label"><strong>Overtime (hrs):</strong></label>
                        <input 
                            type="number" 
                            class="form-control form-control-sm"
                            v-model="formData.ActualOvertime"
                            step="0.25"
                            min="0"
                        />
                    </div>
                    <div class="col-md-3">
                        <label class="form-label"><strong>Should Bill Amount:</strong></label>
                        <div class="input-group input-group-sm">
                            <span class="input-group-text">$</span>
                            <input 
                                type="number" 
                                class="form-control form-control-sm"
                                v-model="formData.ShouldBill"
                                step="0.01"
                                min="0"
                            />
                        </div>
                    </div>
                    <div class="col-md-3">
                        <label class="form-label"><strong>Status:</strong></label>
                        <select 
                            class="form-select form-select-sm"
                            v-model="formData.Status"
                        >
                            <option v-for="status in statusOptions" :key="status.value" :value="status.value">
                                {{ status.label }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Row 6: Invoice Time, Invoice OT, Invoice Amount, Calculate Billing -->
                <div class="row mb-3">
                    <div class="col-md-3">
                        <label class="form-label"><strong>Invoice Time (hrs):</strong></label>
                        <input 
                            type="number" 
                            class="form-control form-control-sm"
                            v-model="formData.InvoiceTime"
                            step="0.25"
                            min="0"
                        />
                    </div>
                    <div class="col-md-3">
                        <label class="form-label"><strong>Invoice OT (hrs):</strong></label>
                        <input 
                            type="number" 
                            class="form-control form-control-sm"
                            v-model="formData.InvoiceOT"
                            step="0.25"
                            min="0"
                        />
                    </div>
                    <div class="col-md-3">
                        <label class="form-label"><strong>Invoice Amount:</strong></label>
                        <div class="input-group input-group-sm">
                            <span class="input-group-text">$</span>
                            <input 
                                type="number" 
                                class="form-control form-control-sm"
                                v-model="formData.InvoiceAmount"
                                step="0.01"
                                min="0"
                            />
                        </div>
                    </div>
                    <div class="col-md-3">
                        <label class="form-label">&nbsp;</label>
                        <button 
                            type="button" 
                            class="btn btn-outline-primary btn-sm w-100"
                            @click="calculateBilling"
                        >
                            <RefreshCw :size="14" class="me-1" />
                            Calculate Billing
                        </button>
                    </div>
                </div>

                <!-- Update Work Details button -->
                <div class="d-flex justify-content-end mb-3">
                    <button 
                        type="button" 
                        class="btn btn-success btn-sm"
                        @click="updateRecord"
                        :disabled="saving"
                    >
                        <Save :size="14" class="me-1" />
                        {{ saving ? 'Saving...' : 'Update Work Details' }}
                    </button>
                </div>

                <!-- Row 4: Inline Add Sections Toggle (may add back...) -->
                <!--
                <div class="row mb-3">
                    <div class="col-12">
                        <div class="d-flex gap-2 p-2 bg-light rounded border">
                            <button type="button" class="btn btn-sm" :class="showFollowUpForm ? 'btn-primary' : 'btn-outline-primary'" @click="showFollowUpForm = !showFollowUpForm">
                                <ClipboardCheck :size="14" class="me-1" />{{ showFollowUpForm ? 'Hide' : 'Add' }} Follow-up
                            </button>
                            <button type="button" class="btn btn-sm" :class="showPartForm ? 'btn-primary' : 'btn-outline-primary'" @click="showPartForm = !showPartForm">
                                <Package :size="14" class="me-1" />{{ showPartForm ? 'Hide' : 'Add' }} Part
                            </button>
                            <button type="button" class="btn btn-sm" :class="showMileageForm ? 'btn-primary' : 'btn-outline-primary'" @click="showMileageForm = !showMileageForm">
                                <Car :size="14" class="me-1" />{{ showMileageForm ? 'Hide' : 'Add' }} Mileage
                            </button>
                            <button type="button" class="btn btn-sm" :class="showExpenseForm ? 'btn-primary' : 'btn-outline-primary'" @click="showExpenseForm = !showExpenseForm">
                                <DollarSign :size="14" class="me-1" />{{ showExpenseForm ? 'Hide' : 'Add' }} Expense
                            </button>
                        </div>
                    </div>
                </div>
                -->

                <!-- Inline Follow-up Task Form -->
                <div>
                    <hr />
                    <h6 class="mb-2"><ClipboardCheck :size="16" class="me-1" />New Follow-up Task</h6>
                    <assignment-form v-model="followUpData"></assignment-form>
                    <div class="d-flex justify-content-end mt-2">
                        <button 
                            type="button" 
                            class="btn btn-sm btn-success"
                            @click="saveFollowUp"
                            :disabled="savingFollowUp"
                        >
                            <Save :size="14" class="me-1" />
                            {{ savingFollowUp ? 'Saving...' : 'Save Follow-up' }}
                        </button>
                    </div>
                </div>

                <!-- Inline Part Form -->
                <div>
                    <hr />
                    <h6 class="mb-2"><Package :size="16" class="me-1" />New Part</h6>
                    <part-form v-model="partData"></part-form>
                    <div class="d-flex justify-content-end mt-2">
                        <button 
                            type="button" 
                            class="btn btn-sm btn-success"
                            @click="savePart"
                            :disabled="savingPart"
                        >
                            <Save :size="14" class="me-1" />
                            {{ savingPart ? 'Saving...' : 'Save Part' }}
                        </button>
                    </div>
                </div>

                <!-- Inline Mileage Form -->
                <div>
                    <hr />
                    <h6 class="mb-2"><Car :size="16" class="me-1" />New Mileage</h6>
                    <mileage-form v-model="mileageData"></mileage-form>
                    <div class="d-flex justify-content-end mt-2">
                        <button 
                            type="button" 
                            class="btn btn-sm btn-success"
                            @click="saveMileageEntry"
                            :disabled="savingMileage"
                        >
                            <Save :size="14" class="me-1" />
                            {{ savingMileage ? 'Saving...' : 'Save Mileage' }}
                        </button>
                    </div>
                </div>

                <!-- Inline Expense Form -->
                <div>
                    <hr />
                    <h6 class="mb-2"><DollarSign :size="16" class="me-1" />New Expense</h6>
                    <expense-form v-model="expenseData"></expense-form>
                    <div class="d-flex justify-content-end mt-2">
                        <button 
                            type="button" 
                            class="btn btn-sm btn-success"
                            @click="saveExpenseEntry"
                            :disabled="savingExpense"
                        >
                            <Save :size="14" class="me-1" />
                            {{ savingExpense ? 'Saving...' : 'Save Expense' }}
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <button type="button" class="btn btn-outline-secondary" @click="$emit('close')">
                <X :size="16" class="me-1" />
                Close
            </button>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import KtModal from '@/components/UI/KtModal.vue';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import ApiService from '@/services/ApiService';
import Emitter from '@/services/Emitter';
import Helper from '@/services/Helper';
import { useTechStore } from '@/stores/TechStore';
import { useTaskStatusStore } from '@/stores/TaskStatusStore';
import { storeToRefs } from 'pinia';
import TechAc from '@/components/Techs/TechAc.vue';
import { X, Save, RefreshCw, ClipboardCheck, Package, Car, DollarSign } from 'lucide-vue-next';
import AssignmentForm from '@/components/Assignments/AssignmentForm.vue';
import PartForm from '@/components/Parts/PartForm.vue';
import MileageForm from '@/components/Mileage/MileageForm.vue';
import ExpenseForm from '@/components/Expenses/ExpenseForm.vue';
import { TimeModel } from '@/models/TaskModel';
import { PartModel } from '@/models/PartModel';
import { MileageModel } from '@/models/MileageModel';
import { ExpenseModel } from '@/models/ExpenseModel';

interface TimeEntry {
    Id: number;
    RequestId?: number;
    TechnicianId?: number;
    AssignedTo?: number;
    TechnicianName?: string;
    CustomerNumber?: number;
    Request?: { Customer?: number };
    WorkCompleteDate?: string;
    Assignment?: string;
    ActualWork?: string;
    ActualTime?: number;
    ActualOvertime?: number;
    ShouldBill?: number;
    Status?: number;
    InvoiceText?: string;
    InvoiceTime?: number;
    InvoiceOT?: number;
    InvoiceAmount?: number;
}

const props = defineProps<{
    visible: boolean;
    timeEntry: TimeEntry | null;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'updated'): void;
}>();

const techStore = useTechStore();
const { list: TechList } = storeToRefs(techStore);

const taskStatusStore = useTaskStatusStore();
const { list: TaskStatusList } = storeToRefs(taskStatusStore);

const saving = ref(false);
const loading = ref(false);
const fullRecord = ref<any>(null); // Store the complete record from API
const formData = ref<TimeEntry>({
    Id: 0,
    RequestId: undefined,
    TechnicianId: undefined,
    CustomerNumber: undefined,
    TechnicianName: '',
    WorkCompleteDate: '',
    Assignment: '',
    ActualWork: '',
    ActualTime: 0,
    ActualOvertime: 0,
    ShouldBill: 0,
    Status: 0,
    InvoiceText: '',
    InvoiceTime: 0,
    InvoiceOT: 0,
    InvoiceAmount: 0
});

const statusOptions = computed(() => {
    return Object.values(TaskStatusList.value).map(status => ({
        label: status.Status,
        value: status.Id
    }));
});

const openJobModal = () => {
    if (formData.value.RequestId) {
        Emitter.emit('edit:job', formData.value.RequestId);
    }
};

const showRateModal = ref(false);
const hourlyRateInput = ref('');

const rollTaskForward = () => {
    if (!formData.value.Assignment) {
        Toast.error('No assignment text to roll forward');
        return;
    }
    
    // Copy assignment text to follow-up WorkAssignment field
    if (followUpData.value) {
        followUpData.value.WorkAssignment = formData.value.Assignment;
        Toast.success('Assignment copied to follow-up task');
    }
};

const calculateBilling = () => {
    const rate = prompt('What is the dollar/hour rate for this task?', '0');
    if (rate === null) return;
    
    const hourlyRate = parseFloat(rate);
    if (isNaN(hourlyRate) || hourlyRate < 0) {
        Toast.error('Please enter a valid hourly rate');
        return;
    }
    
    const actualTime = formData.value.ActualTime || 0;
    const actualOvertime = formData.value.ActualOvertime || 0;
    
    // Round Invoice Time up to nearest quarter hour
    const invoiceTime = Math.ceil(actualTime * 4) / 4;
    formData.value.InvoiceTime = invoiceTime;
    
    // Invoice OT matches Actual OT
    formData.value.InvoiceOT = actualOvertime;
    
    // Calculate billing amounts
    const overtimeRate = hourlyRate * 1.5;
    const totalBill = (invoiceTime * hourlyRate) + (actualOvertime * overtimeRate);
    
    formData.value.ShouldBill = Math.round(totalBill * 100) / 100;
    formData.value.InvoiceAmount = Math.round(totalBill * 100) / 100;
    
    Toast.success(`Calculated: Invoice Time ${invoiceTime}hrs, Amount $${formData.value.InvoiceAmount.toFixed(2)}`);
};

// ===== INLINE FORM STATE =====

// Helper to create a pre-populated TimeModel for follow-up
const createFollowUpModel = () => {
    const rec = new TimeModel();
    rec.RequestId = formData.value.RequestId ? Number(formData.value.RequestId) : undefined;
    rec.CustomerNumber = formData.value.CustomerNumber ? Number(formData.value.CustomerNumber) : undefined;
    rec.AssignedTo = formData.value.TechnicianId ? Number(formData.value.TechnicianId) : undefined;
    return rec;
};

const createPartModel = () => {
    const rec = new PartModel();
    rec.RequestId = formData.value.RequestId ? Number(formData.value.RequestId) : undefined;
    rec.CustomerNumber = formData.value.CustomerNumber ? Number(formData.value.CustomerNumber) : undefined;
    rec.AssignedTo = formData.value.TechnicianId ? Number(formData.value.TechnicianId) : undefined;
    return rec;
};

const createMileageModel = () => {
    const rec = new MileageModel();
    rec.RequestId = formData.value.RequestId ? Number(formData.value.RequestId) : undefined;
    rec.CustomerNumber = formData.value.CustomerNumber ? Number(formData.value.CustomerNumber) : undefined;
    rec.TechnicianId = formData.value.TechnicianId ? Number(formData.value.TechnicianId) : undefined;
    return rec;
};

const createExpenseModel = () => {
    const rec = new ExpenseModel();
    rec.RequestId = formData.value.RequestId ? Number(formData.value.RequestId) : undefined;
    rec.CustomerNumber = formData.value.CustomerNumber ? Number(formData.value.CustomerNumber) : undefined;
    rec.AssignedTo = formData.value.TechnicianId ? Number(formData.value.TechnicianId) : undefined;
    return rec;
};

// Form visibility toggles
const showFollowUpForm = ref(false);
const showPartForm = ref(false);
const showMileageForm = ref(false);
const showExpenseForm = ref(false);

// Form data refs
const followUpData = ref<any>(createFollowUpModel());
const partData = ref<any>(createPartModel());
const mileageData = ref<any>(createMileageModel());
const expenseData = ref<any>(createExpenseModel());

// Saving state
const savingFollowUp = ref(false);
const savingPart = ref(false);
const savingMileage = ref(false);
const savingExpense = ref(false);

// Save handlers
const saveFollowUp = async () => {
    savingFollowUp.value = true;
    try {
        const data = Helper.prep(followUpData.value);
        await ApiService.saveTime(data);
        Toast.success('Follow-up task saved');
        followUpData.value = createFollowUpModel();
    } catch (error) {
        console.error('Error saving follow-up:', error);
        Toast.error('Failed to save follow-up task');
    } finally {
        savingFollowUp.value = false;
    }
};

const savePart = async () => {
    savingPart.value = true;
    try {
        const data = Helper.prep(partData.value);
        await ApiService.savePart(data);
        Toast.success('Part saved');
        partData.value = createPartModel();
    } catch (error) {
        console.error('Error saving part:', error);
        Toast.error('Failed to save part');
    } finally {
        savingPart.value = false;
    }
};

const saveMileageEntry = async () => {
    savingMileage.value = true;
    try {
        const data = Helper.prep(mileageData.value);
        await ApiService.saveMileage(data);
        Toast.success('Mileage saved');
        mileageData.value = createMileageModel();
    } catch (error) {
        console.error('Error saving mileage:', error);
        Toast.error('Failed to save mileage');
    } finally {
        savingMileage.value = false;
    }
};

const saveExpenseEntry = async () => {
    savingExpense.value = true;
    try {
        const data = Helper.prep(expenseData.value);
        await ApiService.saveExpense(data);
        Toast.success('Expense saved');
        expenseData.value = createExpenseModel();
    } catch (error) {
        console.error('Error saving expense:', error);
        Toast.error('Failed to save expense');
    } finally {
        savingExpense.value = false;
    }
};

const loadFullRecord = async (id: number) => {
    loading.value = true;
    try {
        // Load the complete record from the API
        const record = await ApiService.loadTime(id);
        fullRecord.value = record;
        
        // Populate form with all fields from the full record
        formData.value = {
            Id: record.Id,
            RequestId: record.RequestId,
            TechnicianId: record.TechnicianId || record.AssignedTo,
            CustomerNumber: record.CustomerNumber,
            TechnicianName: record.TechnicianName,
            WorkCompleteDate: record.WorkCompleteDate || '',
            Assignment: record.Assignment || '',
            ActualWork: record.ActualWork || '',
            ActualTime: record.ActualTime || 0,
            ActualOvertime: record.ActualOvertime || 0,
            ShouldBill: record.ShouldBill || 0,
            Status: record.Status || 0,
            InvoiceText: record.InvoiceText || '',
            InvoiceTime: record.InvoiceTime || 0,
            InvoiceOT: record.InvoiceOT || 0,
            InvoiceAmount: record.InvoiceAmount || 0
        };
    } catch (error) {
        console.error('Error loading time entry:', error);
        Toast.error('Failed to load time entry');
    } finally {
        loading.value = false;
    }
};

const updateRecord = async () => {
    if (!formData.value.Id || !fullRecord.value) {
        Toast.error('No time entry to update');
        return;
    }

    saving.value = true;
    try {
        // Merge form edits with the full record to preserve all fields
        const updatePayload = {
            ...fullRecord.value, // Start with full record
            // Override with form edits
            WorkCompleteDate: formData.value.WorkCompleteDate || null,
            Assignment: formData.value.Assignment,
            ActualWork: formData.value.ActualWork,
            ActualTime: formData.value.ActualTime,
            ActualOvertime: formData.value.ActualOvertime,
            ShouldBill: formData.value.ShouldBill,
            Status: formData.value.Status,
            InvoiceText: formData.value.InvoiceText,
            InvoiceTime: formData.value.InvoiceTime,
            InvoiceOT: formData.value.InvoiceOT,
            InvoiceAmount: formData.value.InvoiceAmount
        };
        
        await ApiService.saveTime(updatePayload);
        
        Toast.success('Time entry updated successfully');
        emit('updated');
        emit('close');
    } catch (error) {
        console.error('Error updating time entry:', error);
        Toast.error('Failed to update time entry');
    } finally {
        saving.value = false;
    }
};

watch(() => props.timeEntry, (newEntry) => {
    if (newEntry && newEntry.Id) {
        // Load the FULL record from the API instead of using partial data from list
        loadFullRecord(newEntry.Id);

        // Reset inline forms with fresh pre-populated data
        showFollowUpForm.value = false;
        showPartForm.value = false;
        showMileageForm.value = false;
        showExpenseForm.value = false;
        followUpData.value = createFollowUpModel();
        partData.value = createPartModel();
        mileageData.value = createMileageModel();
        expenseData.value = createExpenseModel();
    }
}, { immediate: true });

onMounted(() => {
    techStore.reload();
    taskStatusStore.load();
});
</script>

<style scoped>
.management-review-form {
    min-height: 300px;
}

.form-control-plaintext {
    padding: 0.25rem 0;
    font-weight: 500;
}
</style>
